import DateUtils from "sh-application/utility/DateUtils";
import InstrumentationUtils from "sh-application/utility/InstrumentationUtils";
import MemberUtils from "sh-application/utility/MemberUtils";
import setEmployeeView from "../mutators/setEmployeeView";
import showConfirmDialog from "sh-confirm-dialog/lib/components/ConfirmDialog";
import TagUtils from "sh-application/utility/TagUtils";
import updateScheduleViewDimensions from "../actions/updateScheduleViewDimensions";
import {
    clearScheduleDataInView,
    ScheduleGridUtils,
    setfeatureClickedBeforeDataFetched,
    setGlobalMessageForScheduleLoaded,
    setGroupedView,
    setGroupInView,
    setIsProgressiveRendering,
    setIsViewStatePopulated,
    setMemberInView,
    setScheduleCalendarType,
    setScheduleOverViewType,
    setScheduleSelectedDate,
    setScheduleSelectedRange,
    setupConflicts,
    setupScheduleViewStates,
    updateIsDataInDateRangeLoaded,
    updateNotesInView,
    updateOpenShiftsInView,
    updateShiftsInView,
    setOpenShiftsInView
    } from "../index";
import { DataFilter, DataProcessingHelpers } from "sh-services";
import { DayOfWeek } from "@fluentui/react";
import { DEFAULT_START_OF_WEEK } from "sh-application/../StaffHubConstants";
import {
    ECSConfigKey,
    ECSConfigService,
    InstrumentationService,
    TeamDataService
    } from "sh-services";
import {
    EmployeeViewType,
    EmployeeViewTypes,
    IDataInDateRange,
    INoteEntity,
    IOpenShiftEntity,
    IShiftEntity,
    OpenShiftEntity,
    ScheduleCalendarType,
    ScheduleCalendarTypes,
    ScheduleOverViewType,
    ShiftStates,
    TeamSettingEntity
    } from "sh-models";
import { getGenericEventPropertiesObject, InstrumentationEventPropertyInterface } from "sh-instrumentation";
import { Moment } from "moment";
import { orchestrator } from "satcheljs";
import { restartApp } from "sh-application";
import { runInAction } from "mobx";
import { setGlobalMessageFromException } from "sh-application";
import { setIsConflictCalculationInProgress } from "sh-application/components/schedules/lib";
import { TeamSettingsStore } from "sh-teamsettings-store";
import { TeamStore } from "sh-team-store";
import { trace } from "owa-trace";
import ShiftUtils from "../../../../utility/ShiftUtils";
import { TagStore } from "sh-tag-store";

/**
 * When view dimensions like selected date, overview type, or calendar type are changed, this orchestrator is run.
 * It derives the new schedule range, detects diffs in the view dimensions, fetches schedule data from global cache/service if needed,
 * and then batches updates to the view dimensions so that only one mobx reaction is triggered.
 *
 * Callers triggering the action should pass in null for schedule view dimensions that they do not want to change.
 */

    /**
     * @param viewState - schedule view state
     * @param isTeamBootstrap - set to true if we are bootstrapping a team (eg, during initial app load or switching to a team)
     * @param scheduleCalendarType - schedule calendar type
     * @param scheduleOverViewType - schedule overview type
     * @param selectedDate - current date to view in the schedule view
     * @param isViewGrouped - set to true if the schedule view should be displayed in Grouped mode
     */

export const updateScheduleViewDimensionsOrchestrator =  orchestrator(updateScheduleViewDimensions, async actionMessage => {
        const { viewState, isTeamBootstrap, employeeViewType, scheduleCalendarType, scheduleOverViewType, selectedDate, isViewGrouped, instrumentViewedData } = actionMessage;

        if (!viewState) {
            return;
        }

        // Store previous view dimensions
        const previousViewStart: Moment = viewState.viewStartDate ? viewState.viewStartDate : null;
        const previousViewEnd: Moment = viewState.viewEndDate ? viewState.viewEndDate : null;
        const previousSelectedDate: Moment = viewState.viewSelectedDate ? viewState.viewSelectedDate : null;
        const previousCalendarType: ScheduleCalendarType = viewState.scheduleCalendarType;
        const previousEmployeeViewType: EmployeeViewType = viewState.employeeViewType;
        const previousOverViewType: ScheduleOverViewType = viewState.scheduleOverViewType;
        const previousIsViewGrouped: boolean = viewState.isViewGrouped;

        // Calculate updated view dimensions. Use incoming overrides from parameters or previous values
        // Kill switch: if Fortnight is disabled via ECS, force fallback to Week
        const rawCalendarType: ScheduleCalendarType = scheduleCalendarType || previousCalendarType;
        const updatedCalendarType: ScheduleCalendarType = rawCalendarType === ScheduleCalendarTypes.Fortnight
            && !ECSConfigService.isECSFeatureEnabled(ECSConfigKey.FortnightViewEnabled)
            ? ScheduleCalendarTypes.Week
            : rawCalendarType;
        const updatedEmployeeViewType: EmployeeViewType = employeeViewType || previousEmployeeViewType;
        const updatedOverViewType: ScheduleOverViewType = scheduleOverViewType || previousOverViewType;
        const updatedSelectedDate: Moment = selectedDate || previousSelectedDate;
        const updatedIsViewGrouped: boolean = (isViewGrouped !== null) ? isViewGrouped : previousIsViewGrouped;

        // The updated view start and end are derived from the updated selected date and the schedule calendar type
        const { updatedViewStart, updatedViewEnd } = getUpdatedDateRange(updatedCalendarType, updatedSelectedDate);
        const shouldUseMyShiftsApi = (updatedEmployeeViewType === EmployeeViewTypes.YourShiftsView) && ECSConfigService.isFeatureEnabled(ECSConfigKey.EnableMyShiftsApi);
        let progressiveRendingCompleted = true;
        let isProgressiveRenderingEnabled = false;

        setScheduleSelectedRange(viewState, updatedViewStart, updatedViewEnd);

        try {
            let notes: INoteEntity[] = [];
            let shifts: IShiftEntity[] = [];
            let openShifts: IOpenShiftEntity[] = [];

            const isScheduleRangeChanged = hasScheduleRangeChanged(previousViewStart, previousViewEnd, updatedViewStart, updatedViewEnd);
            const isEmployeeViewChanged = previousEmployeeViewType !== updatedEmployeeViewType;

            // Fetch schedule data from the service if the schedule view range has changed or the employee view has changed
            const shouldLoadScheduleData: boolean = isScheduleRangeChanged || !viewState.isDataInDateRangeLoaded || isEmployeeViewChanged;
            // Determine if we need to force a network call based on the view change
            const forceLoadFromNetwork = isEmployeeViewChanged;

            if (shouldLoadScheduleData) {
                // Clear data loaded flag while the schedule data is being fetched
                updateIsDataInDateRangeLoaded(false, viewState);
                // If we are loading schedule data, set the conflict calculation flag to true to make sure the counting component
                // will show that calculations are in progress. We want to make sure we don't show the count of the previous schedule.
                // This will get set back to false once the calculations are complete.
                setIsConflictCalculationInProgress(true, viewState);
            }

            runInAction(() => {
                // setup all the schedule dimensions - this has to happen even if we aren't loading schedule data
                if (hasCalendarTypeChanged(previousCalendarType, updatedCalendarType)) {
                    setScheduleCalendarType(viewState, updatedCalendarType);
                }

                if (hasEmployeeViewTypeChanged(previousEmployeeViewType, updatedEmployeeViewType)) {
                    setEmployeeView(viewState, updatedEmployeeViewType);
                }

                if (hasOverViewTypeChanged(previousOverViewType, updatedOverViewType)) {
                    setScheduleOverViewType(viewState, updatedOverViewType);
                }

                if (hasSelectedDateChanged(previousSelectedDate, updatedSelectedDate)) {
                    setScheduleSelectedDate(viewState, updatedSelectedDate);
                }

                if (hasIsViewGroupedChanged(previousIsViewGrouped, updatedIsViewGrouped)) {
                    setGroupedView(viewState, updatedIsViewGrouped); // TODO scheduleFilters: remove as this is does not seem to be used
                }
            });

            if (shouldLoadScheduleData && ScheduleGridUtils.isProgressiveRenderingEnabled() && !shouldUseMyShiftsApi) {
                // this flag is to ensure we calculate conflicts correctly when progressive rendering is enabled
                isProgressiveRenderingEnabled = true;

                // ===========================
                // progressive rendering
                // ===========================

                // we page by tags if the view is grouped
                const pageByTag = updatedIsViewGrouped;
                progressiveRendingCompleted = false;

                try {
                    setIsProgressiveRendering(true, true, viewState);

                    let firstRenderMarker = "progressiveRendingFirstTagRender";
                    firstRenderMarker = InstrumentationService.perfMarkerStart(firstRenderMarker);

                    let didComplete = true;
                    let firstDataSet = true;
                    let sortedArray = [];
                    let dataFilter: DataFilter = {};

                    if (pageByTag) {
                        // page by tags
                        sortedArray = TagUtils.getAllTagIds(true /* sortByIndex */);

                        // initialize the DataFilter
                        dataFilter = {
                            tagIds: sortedArray
                        };
                    } else {
                        // page by members
                        sortedArray = MemberUtils.getAllMemberIds(true /* sortByIndex */);

                        // initialize the DataFilter
                        dataFilter = {
                            memberIds: sortedArray
                        };
                    }

                    if (sortedArray && sortedArray.length) {
                        didComplete = await TeamDataService.getDataInDateRangeFiltered(
                            TeamStore().teamId,
                            updatedViewStart,
                            updatedViewEnd,
                            dataFilter,
                            (dataLoaded: DataFilter) => {
                                // this is a callback for each set (tags or memebers) that has been loaded
                                if (dataLoaded.tagIds && dataLoaded.tagIds.length) {
                                    dataLoaded.tagIds.forEach(tagId => {
                                        setGroupInView(tagId);
                                    });
                                } else if (dataLoaded.memberIds && dataLoaded.memberIds.length) {
                                    dataLoaded.memberIds.forEach(memberId => {
                                        setMemberInView(memberId);
                                    });
                                }

                                if (firstDataSet) {
                                    firstDataSet = false;
                                    InstrumentationService.perfMarkerEnd(firstRenderMarker);
                                    updateIsDataInDateRangeLoaded(true, viewState);
                                    if (isTeamBootstrap) {
                                        setIsViewStatePopulated(true, viewState);
                                    }
                                }
                            },
                            forceLoadFromNetwork /* forceLoadFromNetwork */
                        );
                    }

                    if (didComplete) {
                        // only mark as completed if getDataInDateRangeByTags completed, otherwise it was probably canceled and started again.
                        setIsProgressiveRendering(false, true, viewState);
                        setGlobalMessageForScheduleLoaded();

                        // reset the flag for feature clicked irrespective of the data fetched
                        setfeatureClickedBeforeDataFetched(false);
                        progressiveRendingCompleted = true;
                        if (instrumentViewedData && ECSConfigService.isECSFeatureEnabled(ECSConfigKey.InstrumentScheduleViewStatus)) {
                            const openShiftsArray = Array.from(viewState.openShiftsInView.values());
                            const shiftsArray = Array.from(viewState.shiftsInView.values());
                            const members = viewState.employeeViewType == EmployeeViewTypes.YourShiftsView && TeamStore().me ? [TeamStore().me] : DataProcessingHelpers.getArrayFromMap(TeamStore().members);
                            const filteredTags = DataProcessingHelpers.getArrayFromMap(viewState.filteredTags);
                            const filteredMembers = DataProcessingHelpers.getArrayFromMap(viewState.filteredMembers);
                            const selectedShiftFilters = DataProcessingHelpers.getArrayFromMap(viewState.selectedShiftFilters);
                            const isScheduleGroupOrMemberFilterApplied = ScheduleGridUtils.isScheduleGroupOrMemberFilterApplied(filteredTags, filteredMembers);
                            const filteredShiftsArray = shiftsArray.filter(shift => {
                                return !ShiftUtils.isSkippableShiftInSchedule(
                                    shift,
                                    viewState.viewStartDate,
                                    viewState.viewEndDate,
                                    members,
                                    isScheduleGroupOrMemberFilterApplied,
                                    selectedShiftFilters,
                                    filteredTags,
                                    filteredMembers
                                );
                            });

                            const allTags = DataProcessingHelpers.getArrayFromMap(TagStore().tags);
                            const tags = viewState.employeeViewType == EmployeeViewTypes.YourShiftsView && TeamStore().me ? TagUtils.getTagsForMember(allTags, TeamStore().me, shifts) : allTags;
                            const tagIds = tags.map(tag => tag.id);
                            const filteredOpenShiftsArray = openShiftsArray.filter(openShift => {

                                const includeOpenShift = ShiftUtils.isDisplayableOpenShiftInScheduleRange(openShift, viewState.viewStartDate, viewState.viewEndDate);
                                const hasMatchingTag = openShift.tagIds?.some(tagId => tagIds.includes(tagId));
                                return includeOpenShift && hasMatchingTag;
                            });
                            const allShiftsArray = [...filteredShiftsArray, ...filteredOpenShiftsArray];

                            instrumentViewedDataStatus(
                                allShiftsArray.length > 0,
                                viewState.viewStartDate.toISOString(),
                                viewState.viewEndDate.toISOString(),
                                viewState.employeeViewType);
                            }
                        }

                } catch (error) {
                    // Show the error message. Do not re-throw the error
                    // This shouldn't block setting up the ScheduleViewDimensions which will break date navigations and user will never see any schedule data
                    // Should render the empty schedules page
                    setIsProgressiveRendering(false, false /* didComplete */, viewState);
                    updateIsDataInDateRangeLoaded(true, viewState);

                    showConfirmDialog(
                        ScheduleGridUtils.getLoadingFailedErrorDialogTitle() /* error dialog title to show when an error occurs */,
                        ScheduleGridUtils.getLoadingFailedErrordialogSubTitle() /* error dialog sub title to show when an error occurs */,
                        {
                            okText: ScheduleGridUtils.getRefreshButtonText() /* error dialog button to show when an error occurs  OK button is primary button */,
                            hideCancelButton: true,
                            isBlocking: true
                        },
                        () => restartApp() /* on Ok callback */,
                        null,
                        false /* should disable ok button */,
                        false /* show close button on the top right corner  */,
                        false /* call cancel callback on dismiss */
                    );

                    InstrumentationService.logEvent(InstrumentationService.events.LargeTeamsErrors, [
                        getGenericEventPropertiesObject(InstrumentationService.values.SomethingWentWrongError, error),
                        getGenericEventPropertiesObject(
                            InstrumentationService.properties.CurrentView,
                            InstrumentationUtils.getCurrentViewForInstrumentation(scheduleCalendarType)
                        )
                    ]);

                    if (isTeamBootstrap) {
                        // Setup final schedule viewstate settings before marking the schedule viewstate as ready for UX consumption
                        setupScheduleViewStates();
                    }
                }
            } else if (shouldLoadScheduleData) {
                // ===========================
                // not progressive rendering
                // ===========================

                try {
                    let dataInRange: IDataInDateRange = null;

                    // if user is in Your shifts view
                    if (shouldUseMyShiftsApi) {
                        dataInRange = await TeamDataService.getDataInDateRangeMyShifts(
                            TeamStore().teamId,
                            updatedViewStart,
                            updatedViewEnd,
                            forceLoadFromNetwork // forceLoadFromNetwork
                        );
                    }else {
                        dataInRange = await TeamDataService.getDataInDateRange(
                            TeamStore().teamId,
                            updatedViewStart,
                            updatedViewEnd,
                            forceLoadFromNetwork // forceLoadFromNetwork
                        );
                    }

                    notes = dataInRange.notes;
                    shifts = dataInRange.shifts;
                    openShifts = dataInRange.openShifts;
                } catch (error) {
                    // Show the error message. Do not re-throw the error
                    // This shouldn't block setting up the ScheduleViewDimensions which will break date navigations and user will never see any schedule data
                    // Should render the empty schedules page
                    setGlobalMessageFromException(error, false /* autoDismiss */);
                }

                runInAction(() => {
                    // if our new schedule range is not contiguous with the old, we will clear the existing schedule data
                    if (!DateUtils.isRangeContiguous(previousViewStart, previousViewEnd, updatedViewStart, updatedViewEnd)) {
                        clearScheduleDataInView(viewState, false);
                    }

                    // Enable the data loaded flag since schedule data should now be loaded
                    updateIsDataInDateRangeLoaded(true, viewState);

                    // setScheduleSelectedRange(viewState, updatedViewStart, updatedViewEnd);
                    updateNotesInView(notes, viewState);
                    updateShiftsInView(shifts, viewState);
                    if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableAssignOpenShifts)) {
                        // Always replace open shifts so navigating to a period with no open shifts
                        // correctly clears stale entries from the previous range.
                        let openShiftsForView = openShifts;
                        if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.HideZeroSlotOpenShifts)) {
                            // Filter / transform zero-slot open shifts before they reach the view.
                            // This covers the page-load path, which bypasses onOpenShiftsUpdated.
                            // Matches the same rules as onOpenShiftsUpdated:
                            //   - Has a published component → show as asterisk (draft-deleted state)
                            //   - Draft-only (no published component) → omit from view entirely
                            openShiftsForView = openShifts.reduce((acc: IOpenShiftEntity[], os: IOpenShiftEntity) => {
                                if (!os || os.openSlots > 0) {
                                    acc.push(os);
                                } else {
                                    const sharedChanges = os.sharedChanges as IOpenShiftEntity | null | undefined;
                                    if (os.isPublished || (sharedChanges && sharedChanges.isPublished)) {
                                        const draftDeleted = OpenShiftEntity.clone(os);
                                        draftDeleted.state = ShiftStates.Deleted;
                                        draftDeleted.isPublished = false;
                                        draftDeleted.sharedChanges = OpenShiftEntity.clone(os);
                                        acc.push(draftDeleted);
                                    }
                                    // Draft-only (no published component): omit from view entirely
                                }
                                return acc;
                            }, []);
                        }
                        setOpenShiftsInView(openShiftsForView, viewState);
                    } else {
                        updateOpenShiftsInView(openShifts, viewState);
                    }

                    if (instrumentViewedData && ECSConfigService.isECSFeatureEnabled(ECSConfigKey.InstrumentScheduleViewStatus)) {
                        // OpenShifts in view can be in same date range but not in same tags for yourShiftsView.
                        // So filtering out open shifts which are not in the tags available for user.
                        const allTags = DataProcessingHelpers.getArrayFromMap(TagStore().tags);
                        const tags = viewState.employeeViewType == EmployeeViewTypes.YourShiftsView && TeamStore().me ? TagUtils.getTagsForMember(allTags, TeamStore().me, shifts) : allTags;
                        const tagIds = tags.map(tag => tag.id);
                        const filteredOpenShiftsArrayByTags = openShifts.filter(openShift => {
                            const hasMatchingTag = openShift.tagIds?.some(tagId => tagIds.includes(tagId));
                            return  hasMatchingTag;
                        });
                        instrumentViewedDataStatus(
                            shifts.length > 0 || filteredOpenShiftsArrayByTags.length > 0,
                            viewState.viewStartDate.toISOString(),
                            viewState.viewEndDate.toISOString(),
                            viewState.employeeViewType);
                    }
                });
            }

            if (isTeamBootstrap) {
                // Setup final schedule viewstate settings before marking the schedule viewstate as ready for UX consumption
                setupScheduleViewStates();
            }
        } catch (e) {
            // GDIDR errors are handled earlier. There shouldn't be any other errors happening at this stage
            trace.warn(`updateScheduleViewDimensions is failing with error: ${e}`);
        } finally {
            const shouldForceCalculate = isProgressiveRenderingEnabled && progressiveRendingCompleted;
            // We need to check if conflict calculation needs to be updated or
            // newly calculated for a different set of shifts, after any date range changes
            if (progressiveRendingCompleted) {
                setupConflicts(
                    viewState,
                    null /* shiftsAdded */,
                    null /* shiftsDeleted */,
                    null /* shiftsUpdated */,
                    viewState.viewStartDate /* updatedViewStartTime */,
                    viewState.viewEndDate /* updatedViewEndTime */,
                    shouldForceCalculate /* force calculate */,
                    false /* non-PR mode */,
                    viewState.showShiftConflicts /* user settings enabled */);
            }
        }
    }
);

// Helpers
export function getUpdatedDateRange(updatedCalendarType: ScheduleCalendarType, updatedSelectedDate: Moment): { updatedViewStart: Moment, updatedViewEnd: Moment} {
    // The updated view start and end are derived from the updated selected date and the schedule calendar type
    let updatedViewStart: Moment = null;
    let updatedViewEnd: Moment = null;

    // Update the start and end of selection based on the previous selected date and the overview type
    switch (updatedCalendarType) {
        case ScheduleCalendarTypes.Day: {
            updatedViewStart = updatedSelectedDate.clone().startOf("day");
            updatedViewEnd = updatedSelectedDate.clone().endOf("day");
            break;
        }
        case ScheduleCalendarTypes.Week: {
            // Compute the previous week range for the selected date, taking the start day of week setting into account.
            const startDayOfWeekStoreValue: string = TeamSettingEntity.getValueOrDefault(TeamSettingsStore().startingDayOfWeek) || DEFAULT_START_OF_WEEK;
            const startDayOfWeek: number = DayOfWeek[startDayOfWeekStoreValue as keyof typeof DayOfWeek];
            updatedViewStart = DateUtils.getWeekStartEndForDate(updatedSelectedDate, startDayOfWeek, true /* doGetWeekStart */);
            updatedViewEnd = DateUtils.getWeekStartEndForDate(updatedSelectedDate, startDayOfWeek, false /* doGetWeekStart */);
            break;
        }
        case ScheduleCalendarTypes.Fortnight: {
            // Compute a 2-week (14-day) range anchored to the team's start day of week.
            const fortnightStartDayOfWeekStoreValue: string = TeamSettingEntity.getValueOrDefault(TeamSettingsStore().startingDayOfWeek) || DEFAULT_START_OF_WEEK;
            const fortnightStartDayOfWeek: number = DayOfWeek[fortnightStartDayOfWeekStoreValue as keyof typeof DayOfWeek];
            updatedViewStart = DateUtils.getWeekStartEndForDate(updatedSelectedDate, fortnightStartDayOfWeek, true /* doGetWeekStart */);
            updatedViewEnd = updatedViewStart.clone().add(13, 'days').endOf('day');
            break;
        }
        case ScheduleCalendarTypes.Month: {
            updatedViewStart = updatedSelectedDate.clone().startOf("month");
            updatedViewEnd = updatedSelectedDate.clone().endOf("month");
            break;
        }
    }

    return { updatedViewStart: updatedViewStart, updatedViewEnd: updatedViewEnd };
}

export function hasSelectedDateChanged(previousSelectedDate: Moment, updatedSelectedDate: Moment): boolean {
    return !previousSelectedDate.isSame(updatedSelectedDate) || previousSelectedDate.tz() !== updatedSelectedDate.tz();
}

export function hasScheduleRangeChanged(previousViewStart: Moment, previousViewEnd: Moment, updatedViewStart: Moment, updatedViewEnd: Moment): boolean {
    return !previousViewStart.isSame(updatedViewStart) || !previousViewEnd.isSame(updatedViewEnd) || previousViewStart.tz() !== updatedViewStart.tz() || previousViewEnd.tz() !== updatedViewEnd.tz();
}

export function hasCalendarTypeChanged(previousCalendarType: ScheduleCalendarType, updatedCalendarType: ScheduleCalendarType): boolean {
    return previousCalendarType !== updatedCalendarType;
}

export function hasEmployeeViewTypeChanged(previousEmployeeViewType: EmployeeViewType, updatedEmployeeViewType: EmployeeViewType): boolean {
    return previousEmployeeViewType !== updatedEmployeeViewType;
}

export function hasOverViewTypeChanged(previousOverViewType: ScheduleOverViewType, updatedOverViewType: ScheduleOverViewType): boolean {
    return previousOverViewType !== updatedOverViewType;
}

export function hasIsViewGroupedChanged(previousIsViewGrouped: boolean, updatedIsViewGrouped: boolean): boolean {
    return previousIsViewGrouped !== updatedIsViewGrouped;
}

function instrumentViewedDataStatus(areShiftsInView: boolean, viewStartDate: string, viewEndDate: string, selectedEmployeeViewType: EmployeeViewType) {

    const eventProperties: Array<InstrumentationEventPropertyInterface> = [];
    const viewTypeEvent = (selectedEmployeeViewType == EmployeeViewTypes.YourShiftsView) ? InstrumentationService.events.YourShiftsView : InstrumentationService.events.TeamShiftsView;
    eventProperties.push(getGenericEventPropertiesObject(InstrumentationService.properties.ScreenName, InstrumentationService.ShiftScreenNames.SchedulesPage));
    eventProperties.push(
        getGenericEventPropertiesObject(InstrumentationService.convertToDataBag(InstrumentationService.properties.hasContentDisplayed), areShiftsInView)
    );
    eventProperties.push(
        getGenericEventPropertiesObject(InstrumentationService.properties.ViewStartDate, viewStartDate)
    );
    eventProperties.push(
        getGenericEventPropertiesObject(InstrumentationService.properties.ViewEndDate, viewEndDate)
    );
    eventProperties.push(getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.eventTypes.Viewed));
    InstrumentationService.logEvent(viewTypeEvent, eventProperties);

}